import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Search } from 'lucide-react';
import Card from '../../../components/ui/Card';
import { translateTerm as translateTermUtil, fmtBRL, getRolePriority, getAge as getAgeUtil } from '../utils';

export default function GovernanceTab({ lang, year, petrobrasData, refDate, t }) {
  const translateTerm = (term) => translateTermUtil(term, lang);
  const getAge = (birthdateStr) => getAgeUtil(birthdateStr, year, lang);

  const [boardSearch, setBoardSearch] = useState('');

  const filteredBoard = useMemo(() => {
    const board = petrobrasData['administrador_membro_conselho_fiscal'] || [];
    let list = board;
    if (boardSearch.trim()) {
      const term = boardSearch.toLowerCase();
      list = board.filter(m =>
        (m.Nome && m.Nome.toLowerCase().includes(term)) ||
        (m.Orgao_Administracao && m.Orgao_Administracao.toLowerCase().includes(term)) ||
        (m.Cargo_Eletivo_Ocupado && m.Cargo_Eletivo_Ocupado.toLowerCase().includes(term)) ||
        (m.Profissao && m.Profissao.toLowerCase().includes(term))
      );
    }
    return [...list].sort((a, b) => getRolePriority(a.Cargo_Eletivo_Ocupado) - getRolePriority(b.Cargo_Eletivo_Ocupado));
  }, [boardSearch, petrobrasData]);

  const sortedCommittees = useMemo(() => {
    const committees = petrobrasData['membro_comite'] || [];
    return [...committees].sort((a, b) => getRolePriority(a.Cargo_Ocupado) - getRolePriority(b.Cargo_Ocupado));
  }, [petrobrasData]);

  const ORGAN_COLORS = { 'Diretoria Estatutária': '#10b981', 'Conselho de Administração': '#3b82f6', 'Conselho Fiscal': '#f59e0b', 'Comitê': '#8b5cf6' };
  const organColor = (name) => ORGAN_COLORS[name] || '#6b7280';

  return (
    <div className="space-y-6">

      {/* Responsáveis pelo Envio */}
      <Card title={lang === 'pt' ? 'Responsáveis pelo Envio do FRE' : 'FRE Filing Responsibles'} refDate={refDate}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(petrobrasData['responsavel'] || []).map((r, idx) => {
            const initials = r.Nome_Responsavel
              ? r.Nome_Responsavel.split(' ').filter(n => n.length > 2).slice(0, 2).map(n => n[0].toUpperCase()).join('')
              : 'R';
            return (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] bg-[var(--accent-soft)]/20">
                <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-black text-xs shadow-sm">
                  {initials}
                </div>
                <div>
                  <span className="font-bold text-sm text-[var(--text)] block">{r.Nome_Responsavel || '-'}</span>
                  <span className="text-[10px] text-emerald-400 font-semibold">{translateTerm(r.Cargo_Responsavel)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Remuneração */}
      {(() => {
        const remTotal = petrobrasData['remuneracao_total_orgao'] || [];
        const remMed   = petrobrasData['remuneracao_maxima_minima_media'] || [];
        const remAcao  = petrobrasData['remuneracao_acao'] || [];
        const remVar   = petrobrasData['remuneracao_variavel'] || [];

        const getPeriods = (rows) => [...new Set(rows.map(r => r.Data_Fim_Exercicio_Social))].sort();
        const latestOf = (rows) => { const p = getPeriods(rows); return p[p.length - 1]; };

        const latestTotal = remTotal.length ? latestOf(remTotal) : null;
        const latestMed   = remMed.length   ? latestOf(remMed)   : null;

        const totalRows = latestTotal ? remTotal.filter(r => r.Data_Fim_Exercicio_Social === latestTotal) : [];
        const medRows   = latestMed   ? remMed.filter(r => r.Data_Fim_Exercicio_Social === latestMed)     : [];

        const barData = totalRows.map(r => ({
          orgao: translateTerm(r.Orgao_Administracao),
          [lang === 'pt' ? 'Salário/Honorários' : 'Salary/Fees']: +(parseFloat(r.Salario) / 1e6).toFixed(2),
          [lang === 'pt' ? 'Baseada em Ações' : 'Stock-Based']: +(parseFloat(r.Baseada_Acoes) / 1e6).toFixed(2),
          [lang === 'pt' ? 'Pós-Emprego' : 'Post-Employment']: +(parseFloat(r.Pos_emprego) / 1e6).toFixed(2),
          [lang === 'pt' ? 'Cessação de Cargo' : 'Termination']: +(parseFloat(r.Cessacao_Cargo) / 1e6).toFixed(2),
          total: +(parseFloat(r.Total_Remuneracao_Orgao) / 1e6).toFixed(2),
        }));

        const grandTotal = totalRows.reduce((s, r) => s + (parseFloat(r.Total_Remuneracao_Orgao) || 0), 0);

        if (!totalRows.length && !medRows.length) return null;

        return (
          <div className="space-y-6">
            <Card
              title={lang === 'pt' ? 'Remuneração Total por Órgão' : 'Total Compensation by Body'}
              subtitle={lang === 'pt' ? `Período: ${latestTotal} · Total: ${fmtBRL(grandTotal)}` : `Period: ${latestTotal} · Total: ${fmtBRL(grandTotal)}`}
              refDate={refDate}
            >
              <div className="h-56 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="orgao" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => `R$${v}M`} />
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} formatter={(v) => [`R$ ${v}M`]} />
                    <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                    <Bar dataKey={lang === 'pt' ? 'Salário/Honorários' : 'Salary/Fees'} stackId="a" fill="#10b981" />
                    <Bar dataKey={lang === 'pt' ? 'Baseada em Ações' : 'Stock-Based'} stackId="a" fill="#3b82f6" />
                    <Bar dataKey={lang === 'pt' ? 'Pós-Emprego' : 'Post-Employment'} stackId="a" fill="#f59e0b" />
                    <Bar dataKey={lang === 'pt' ? 'Cessação de Cargo' : 'Termination'} stackId="a" fill="#ec4899" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs min-w-[700px]">
                  <thead>
                    <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Órgão' : 'Body'}</th>
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Membros' : 'Members'}</th>
                      <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Salário' : 'Salary'}</th>
                      <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Benefícios' : 'Benefits'}</th>
                      <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Baseada em Ações' : 'Stock-Based'}</th>
                      <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Cessação' : 'Termination'}</th>
                      <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Total' : 'Total'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {totalRows.map((r, idx) => (
                      <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                        <td className="p-3">
                          <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider border"
                            style={{ color: organColor(r.Orgao_Administracao), backgroundColor: `${organColor(r.Orgao_Administracao)}15`, borderColor: `${organColor(r.Orgao_Administracao)}30` }}>
                            {translateTerm(r.Orgao_Administracao)}
                          </span>
                        </td>
                        <td className="p-3 text-[var(--muted)]">{r.Numero_Membros_Remunerados}/{r.Numero_Membros}</td>
                        <td className="p-3 text-right text-[var(--text)]">{fmtBRL(r.Salario)}</td>
                        <td className="p-3 text-right text-[var(--text)]">{fmtBRL(r.Beneficios_Diretos_Indiretos)}</td>
                        <td className="p-3 text-right text-[var(--text)]">{fmtBRL(r.Baseada_Acoes)}</td>
                        <td className="p-3 text-right text-[var(--text)]">{fmtBRL(r.Cessacao_Cargo)}</td>
                        <td className="p-3 text-right font-bold text-emerald-500">{fmtBRL(r.Total_Remuneracao_Orgao)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {medRows.length > 0 && (
              <Card
                title={lang === 'pt' ? 'Remuneração Individual: Máx / Mín / Média' : 'Individual Compensation: Max / Min / Avg'}
                subtitle={lang === 'pt' ? `Período: ${latestMed}` : `Period: ${latestMed}`}
                refDate={refDate}
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {medRows.map((r, idx) => (
                    <div key={idx} className="border border-[var(--border)] rounded-xl p-4 space-y-3">
                      <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider border block w-fit"
                        style={{ color: organColor(r.Orgao_Administracao), backgroundColor: `${organColor(r.Orgao_Administracao)}15`, borderColor: `${organColor(r.Orgao_Administracao)}30` }}>
                        {translateTerm(r.Orgao_Administracao)}
                      </span>
                      <div className="text-xs text-[var(--muted)] mb-1">
                        {r.Numero_Membros_Remunerados}/{r.Numero_Membros} {lang === 'pt' ? 'membros remunerados' : 'members compensated'}
                      </div>
                      {[
                        { label: lang === 'pt' ? 'Maior' : 'Highest', val: r.Valor_Maior_Remuneracao, color: 'text-emerald-500' },
                        { label: lang === 'pt' ? 'Média' : 'Average', val: r.Valor_Medio_Remuneracao, color: 'text-blue-400' },
                        { label: lang === 'pt' ? 'Menor' : 'Lowest', val: r.Valor_Menor_Remuneracao, color: 'text-yellow-500' },
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-[10px] text-[var(--muted)]">{item.label}</span>
                          <span className={`text-xs font-bold ${item.color}`}>{fmtBRL(item.val)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {(remAcao.length === 0 && remVar.length === 0) && (
              <div className="text-[10px] text-[var(--muted)] bg-[var(--accent-soft)]/30 border border-[var(--border)] rounded-xl px-4 py-3">
                {lang === 'pt'
                  ? 'ℹ Remuneração baseada em ações (planos de opções/ações restritas) e remuneração variável não registradas no FRE para este período.'
                  : 'ℹ Stock-based awards (option/restricted share plans) and variable compensation are not registered in FRE for this period.'}
              </div>
            )}
          </div>
        );
      })()}

      {/* Board Members */}
      <Card title={t.card_board_members} refDate={refDate}>
        <div className="relative mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" size={16} />
          <input
            type="text"
            value={boardSearch}
            onChange={(e) => setBoardSearch(e.target.value)}
            placeholder={t.search_placeholder}
            className="w-full bg-[var(--accent-soft)] border border-[var(--border)] rounded-xl py-2.5 pl-10 pr-4 text-sm text-[var(--text)] placeholder-[var(--muted)] focus:outline-none focus:border-emerald-500 transition-colors"
          />
        </div>

        <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs min-w-[800px]">
            <thead>
              <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                <th className="p-3.5 font-semibold">{t.th_organ}</th>
                <th className="p-3.5 font-semibold">{t.th_name}</th>
                <th className="p-3.5 font-semibold">{t.th_position}</th>
                <th className="p-3.5 font-semibold">{t.th_profession}</th>
                <th className="p-3.5 font-semibold">{t.th_elected_by}</th>
                <th className="p-3.5 font-semibold">{t.th_term}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filteredBoard.length > 0 ? (
                filteredBoard.map((m, idx) => {
                  const prio = getRolePriority(m.Cargo_Eletivo_Ocupado);
                  const isPres = prio === 1;
                  const isVP = prio === 2;
                  let rowClass = 'hover:bg-[var(--accent-soft)]/20 transition-colors';
                  let firstCellClass = 'p-3.5';
                  if (isPres) { rowClass = 'bg-amber-500/[0.04] hover:bg-amber-500/[0.08] transition-colors'; firstCellClass = 'p-3.5 border-l-2 border-amber-500/70 pl-3.5'; }
                  else if (isVP) { rowClass = 'bg-indigo-500/[0.04] hover:bg-indigo-500/[0.08] transition-colors'; firstCellClass = 'p-3.5 border-l-2 border-indigo-500/70 pl-3.5'; }
                  return (
                    <tr key={idx} className={rowClass}>
                      <td className={firstCellClass}>
                        <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                          {translateTerm(m.Orgao_Administracao)}
                        </span>
                      </td>
                      <td className="p-3.5 font-bold text-[var(--text)]">{m.Nome || '-'}</td>
                      <td className="p-3.5 text-[var(--text)]">
                        {isPres ? (
                          <span className="bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">{translateTerm(m.Cargo_Eletivo_Ocupado)}</span>
                        ) : isVP ? (
                          <span className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-500 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">{translateTerm(m.Cargo_Eletivo_Ocupado)}</span>
                        ) : (
                          translateTerm(m.Cargo_Eletivo_Ocupado)
                        )}
                      </td>
                      <td className="p-3.5 text-[var(--muted)]">{translateTerm(m.Profissao)}</td>
                      <td className="p-3.5 text-[var(--muted)]">{translateTerm(m.Eleito_Controlador)}</td>
                      <td className="p-3.5 text-[var(--text)]">{m.Prazo_Mandato || '-'}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="6" className="p-8 text-center text-[var(--muted)]">{t.l_empty}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Committees */}
      <Card title={t.card_committees} refDate={refDate}>
        <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs min-w-[700px]">
            <thead>
              <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                <th className="p-3.5 font-semibold">{t.th_member_name}</th>
                <th className="p-3.5 font-semibold">{t.th_committee_type}</th>
                <th className="p-3.5 font-semibold">{t.th_position}</th>
                <th className="p-3.5 font-semibold">{t.th_profession}</th>
                <th className="p-3.5 font-semibold">{t.th_age}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {sortedCommittees.map((c, idx) => {
                const prio = getRolePriority(c.Cargo_Ocupado);
                const isPres = prio === 1;
                const isVP = prio === 2;
                let rowClass = 'hover:bg-[var(--accent-soft)]/20 transition-colors';
                let firstCellClass = 'p-3.5 font-bold text-[var(--text)]';
                if (isPres) { rowClass = 'bg-amber-500/[0.04] hover:bg-amber-500/[0.08] transition-colors'; firstCellClass = 'p-3.5 font-bold text-[var(--text)] border-l-2 border-amber-500/70 pl-3.5'; }
                else if (isVP) { rowClass = 'bg-indigo-500/[0.04] hover:bg-indigo-500/[0.08] transition-colors'; firstCellClass = 'p-3.5 font-bold text-[var(--text)] border-l-2 border-indigo-500/70 pl-3.5'; }
                return (
                  <tr key={idx} className={rowClass}>
                    <td className={firstCellClass}>{c.Nome || '-'}</td>
                    <td className="p-3.5 font-semibold text-emerald-500">{translateTerm(c.Tipo_Comite)}</td>
                    <td className="p-3.5 text-[var(--text)]">
                      {isPres ? (
                        <span className="bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">{translateTerm(c.Cargo_Ocupado)}</span>
                      ) : isVP ? (
                        <span className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-500 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">{translateTerm(c.Cargo_Ocupado)}</span>
                      ) : (
                        translateTerm(c.Cargo_Ocupado)
                      )}
                    </td>
                    <td className="p-3.5 text-[var(--muted)]">{translateTerm(c.Profissao)}</td>
                    <td className="p-3.5 text-[var(--muted)]">{getAge(c.Data_Nascimento)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

    </div>
  );
}
